// The UI theme row of OPTIONS · INTERFACE (themes.md §2, R1776).
//
// The row is a stepper over the UI theme values the account may use: AUTO,
// the five faction themes, each vendored UI theme whose key the account
// holds, and CUSTOM when the account holds `ui_themes` AND stores a UI
// code. A value the account cannot use is not in the list, so a step
// always stores its value. The code field, the overview and the tier tags
// stay in the hub MODDING tab (R807). A stored pick that is not in the
// list (a lapsed CUSTOM, a keyed theme of a lapsed key) still shows on the
// row, and the first step leaves it.
//
// This module decides the values and the next pick. A host writes the
// pick through `setThemePick`, which also keeps the theme link in step.

import { KEY_UI_THEMES } from '../../gen/balance';
import { VENDORED_THEMES } from '../../gen/presentation';
import { options as S } from '../../gen/strings';
import { factionName } from '../../world';
import { CUSTOM, type GameSettings } from '../settings';
import { THEME_CHOICES } from '../theme';
import type { Choice } from './kit';

/** An id as a person reads it: `graphite` becomes GRAPHITE. */
function readable(id: string): string {
  return id.replace(/[-_]/g, ' ').toUpperCase();
}

/** The values the account may step through, in the order of the MODDING tab. */
export function uiThemeSteps(s: GameSettings, held: ReadonlySet<string>): Choice[] {
  const out: Choice[] = THEME_CHOICES.map((t) => ({
    id: String(t),
    label: t === 'auto' ? S.theme_auto : factionName(t).toUpperCase(),
  }));
  for (const e of VENDORED_THEMES.ui) {
    if (!e.key || held.has(e.key)) out.push({ id: e.id, label: readable(e.id) });
  }
  if (held.has(KEY_UI_THEMES) && s.codes.ui !== '') {
    out.push({ id: CUSTOM, label: S.theme_custom({ code: s.codes.ui }) });
  }
  return out;
}

/** The label of the stored pick, in the list or out of it. */
export function uiThemeLabel(s: GameSettings, held: ReadonlySet<string>): string {
  const id = String(s.theme);
  const hit = uiThemeSteps(s, held).find((c) => c.id === id);
  if (hit) return hit.label;
  if (id === CUSTOM) return s.codes.ui ? S.theme_custom({ code: s.codes.ui }) : S.theme_custom_none;
  return readable(id);
}

/**
 * The pick one arrow press asks for: `dir` -1 for the left arrow, else 1.
 * The list wraps round. A stored pick out of the list steps to the first
 * value (or, to the left, the last). Null while the list holds one value.
 */
export function stepUiTheme(s: GameSettings, held: ReadonlySet<string>, dir: number): string | null {
  const list = uiThemeSteps(s, held);
  if (list.length < 2) return null;
  const at = list.findIndex((c) => c.id === String(s.theme));
  const step = dir < 0 ? -1 : 1;
  const next = at < 0
    ? (step < 0 ? list.length - 1 : 0)
    : (at + step + list.length) % list.length;
  return list[next].id;
}
